import React, { useState } from 'react';
import {
  Modal,
  Fade,
  Backdrop,
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import { useIntl } from 'react-intl';
import { useThemeMode } from '@/contexts/ThemeProvider';
import { THEME_OPTIONS } from '@/constants';
import { ThemeMode } from '@/types';
import { useAppUser } from '@/contexts/AppUserProvider';
import { X } from '@phosphor-icons/react';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' }
];

const WEEKDAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'sunday', label: 'Sunday' },
  { value: 'saturday', label: 'Saturday' }
];

// Shared input styling for Attio-style inputs
const getInputStyles = (isDark: boolean) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '14px',
    borderRadius: '8px',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fff',
    '& input': {
      py: 1,
      px: 1.5
    },
    '& fieldset': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      borderWidth: '1px'
    },
    '&:hover fieldset': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
    },
    '&.Mui-focused fieldset': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      borderWidth: '1px'
    }
  }
});

// Shared select styling
const getSelectStyles = (isDark: boolean) => ({
  fontSize: '14px',
  borderRadius: '8px',
  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fff',
  '& .MuiSelect-select': {
    py: 1,
    px: 1.5
  },
  '& fieldset': {
    borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    borderWidth: '1px'
  },
  '&:hover fieldset': {
    borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
  },
  '&.Mui-focused fieldset': {
    borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    borderWidth: '1px'
  }
});

// Shared label styling
const getLabelStyles = (isDark: boolean) => ({
  mb: 0.75,
  fontSize: '13px',
  fontWeight: 400,
  color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'
});

export const SettingsModal: React.FC<SettingsModalProps> = ({
  open,
  onClose
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { mode, setMode } = useThemeMode();
  const { appUser } = useAppUser();
  
  const [firstName, setFirstName] = useState(appUser?.firstName || '');
  const [lastName, setLastName] = useState(appUser?.lastName || '');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [startWeekOn, setStartWeekOn] = useState('monday');
  const [isSaving, setIsSaving] = useState(false);

  const inputSx = getInputStyles(isDark);
  const selectSx = getSelectStyles(isDark);
  const labelSx = getLabelStyles(isDark);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      // TODO: Call API to update user profile
      console.log('Saving profile:', { firstName, lastName, timezone, startWeekOn });
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{
        backdrop: Backdrop
      }}
      slotProps={{
        backdrop: { timeout: 300 }
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: isDark
              ? '0 24px 48px rgba(0, 0, 0, 0.4)'
              : '0 24px 48px rgba(0, 0, 0, 0.12)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: 520,
            maxHeight: '85vh',
            overflow: 'hidden',
            outline: 'none',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2.5,
              borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
              Settings
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                borderRadius: '8px',
                color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              <X size={18} weight="regular" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ px: 3, py: 3, overflow: 'auto', maxHeight: 'calc(85vh - 140px)' }}>
            {/* Profile Section */}
            <Box>
              <Typography variant="body1" fontWeight={600} mb={0.5} sx={{ fontSize: '15px' }}>
                Profile
              </Typography>
              <Typography variant="body2" sx={{ ...labelSx, mb: 2.5 }}>
                Changes to your profile will apply to all of your workspaces.
              </Typography>

              {/* First and Last Name */}
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                <Box>
                  <Typography variant="body2" sx={labelSx}>
                    First Name
                  </Typography>
                  <TextField
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Sam"
                    fullWidth
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={labelSx}>
                    Last Name
                  </Typography>
                  <TextField
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Herro"
                    fullWidth
                    sx={inputSx}
                  />
                </Box>
              </Box>

              {/* Email */}
              <Box>
                <Typography variant="body2" sx={labelSx}>
                  Primary Email Address
                </Typography>
                <Box
                  sx={{
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fff',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px'
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    {appUser?.email || 'sam@horizen.ag'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

            {/* Time Preferences */}
            <Box>
              <Typography variant="body1" fontWeight={600} mb={0.5} sx={{ fontSize: '15px' }}>
                Time preferences
              </Typography>
              <Typography variant="body2" sx={{ ...labelSx, mb: 2 }}>
                Manage your time preferences
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="body2" sx={labelSx}>
                    Preferred Timezone
                  </Typography>
                  <Select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    fullWidth
                    sx={selectSx}
                  >
                    {TIMEZONES.map((tz) => (
                      <MenuItem key={tz.value} value={tz.value} sx={{ fontSize: '14px' }}>
                        {tz.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <Typography variant="body2" sx={labelSx}>
                    Start week on
                  </Typography>
                  <Select
                    value={startWeekOn}
                    onChange={(e) => setStartWeekOn(e.target.value)}
                    fullWidth
                    sx={selectSx}
                  >
                    {WEEKDAYS.map((day) => (
                      <MenuItem key={day.value} value={day.value} sx={{ fontSize: '14px' }}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

            {/* Theme Section */}
            <Box>
              <Typography variant="body1" fontWeight={600} mb={2} sx={{ fontSize: '15px' }}>
                Theme
              </Typography>
              <Box>
                <Typography variant="body2" sx={labelSx}>
                  {intl.formatMessage({ id: 'sidebar.theme' })}
                </Typography>
                <Select
                  value={mode ?? 'system'}
                  onChange={(e) => setMode(e.target.value as ThemeMode)}
                  fullWidth
                  sx={selectSx}
                >
                  {THEME_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 3,
              py: 2,
              borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
            }}
          >
            <Button
              onClick={onClose}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                py: 0.625,
                px: 1.5,
                borderRadius: '6px',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
                }
              }}
            >
              Cancel
              <Typography
                component="span"
                sx={{
                  ml: 0.75,
                  fontSize: '10px',
                  color: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
                  borderRadius: '3px',
                  px: 0.5,
                  py: 0.125
                }}
              >
                ESC
              </Typography>
            </Button>
            <Button
              onClick={handleSaveProfile}
              disabled={isSaving}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '6px',
                py: 0.625,
                px: 2,
                backgroundColor: isDark ? '#fff' : '#1A1A1A',
                color: isDark ? '#000' : '#fff',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
                  boxShadow: 'none'
                }
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
              <Typography
                component="span"
                sx={{
                  ml: 0.75,
                  fontSize: '12px',
                  opacity: 0.7
                }}
              >
                ↵
              </Typography>
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
